/**
 * Files (ADR-0049): the person's cloud storage, through the API. Listings,
 * links and sign-ins are JSON through the BFF's API proxy; a file put into
 * storage streams through the BFF's own upload route, with progress.
 *
 * Every file is scanned for malware by the API before it lands, downloads
 * included, so a refusal can name a virus: show the API's message as it is.
 */

import type {
  StorageConnectStart,
  StorageConnectStatus,
  StorageLink,
  StorageListing,
  StorageOverview,
  StorageSaved,
} from 'zitimail-shared-types';

import { api, ApiError } from '../admin/api';

const base = (slug: string) => `/tenants/${encodeURIComponent(slug)}/storage`;

export const storage = {
  overview: (slug: string, signal?: AbortSignal) =>
    api<StorageOverview>(base(slug), signal ? { signal } : {}),
  connect: (slug: string, serverId: string, label?: string) =>
    api<StorageConnectStart>(`${base(slug)}/connect`, {
      method: 'POST',
      body: { server_id: serverId, ...(label ? { label } : {}) },
    }),
  status: (slug: string, attempt: string) =>
    api<StorageConnectStatus>(`${base(slug)}/connect/${encodeURIComponent(attempt)}`),
  rename: (slug: string, id: string, label: string) =>
    api<void>(`${base(slug)}/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { label },
    }),
  makeDefault: (slug: string, id: string) =>
    api<void>(`${base(slug)}/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { default: true },
    }),
  disconnect: (slug: string, id: string) =>
    api<void>(`${base(slug)}/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  items: (slug: string, id: string, path: string, signal?: AbortSignal) =>
    api<StorageListing>(
      `${base(slug)}/accounts/${encodeURIComponent(id)}/items?path=${encodeURIComponent(path)}`,
      signal ? { signal } : {},
    ),
  link: (slug: string, id: string, path: string) =>
    api<StorageLink>(`${base(slug)}/accounts/${encodeURIComponent(id)}/link`, {
      method: 'POST',
      body: { path },
    }),
  /** A plain link to the file: the browser downloads it (scanned first). */
  downloadUrl: (slug: string, id: string, path: string) =>
    `/api/v1${base(slug)}/accounts/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`,
};

/** The filename a `Content-Disposition` header names, UTF-8 form first. */
export function dispositionName(header: string | null, fallback: string): string {
  if (!header) {
    return fallback;
  }
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // Fall through to the plain name.
    }
  }
  const plain = /filename="([^"]*)"/i.exec(header);
  return plain?.[1] || fallback;
}

/**
 * A file from storage, as a File to attach: fetched (and scanned by the API,
 * which also refuses programs and scripts as attachments).
 */
export async function fetchForAttachment(
  slug: string,
  id: string,
  path: string,
  name: string,
): Promise<File> {
  const response = await fetch(`${storage.downloadUrl(slug, id, path)}&purpose=attach`, {
    credentials: 'include',
    headers: { 'X-Genworx-Request': '1' },
  });
  if (!response.ok) {
    let message = 'the file could not be fetched';
    let code = 'failed';
    try {
      const body = (await response.json()) as { message?: string; error?: string };
      message = body.message ?? message;
      code = body.error ?? code;
    } catch {
      // Not JSON: keep the general message.
    }
    if (response.status === 401) {
      window.dispatchEvent(new Event('genworx:session-ended'));
    }
    throw new ApiError(response.status, code, message);
  }
  const blob = await response.blob();
  return new File([blob], dispositionName(response.headers.get('content-disposition'), name), {
    type: blob.type || 'application/octet-stream',
  });
}

/** A save in progress. */
export interface StorageUpload {
  readonly done: Promise<StorageSaved>;
  readonly abort: () => void;
}

/**
 * Puts a file into a folder of the person's storage, streamed through the
 * BFF; the API scans it before it goes on. `source` is for the audit:
 * `attachment` when saving a received attachment.
 */
export function uploadToStorage(
  accountId: string,
  folder: string,
  file: Blob,
  name: string,
  onProgress: (fraction: number) => void,
  source: 'upload' | 'attachment' = 'upload',
): StorageUpload {
  const xhr = new XMLHttpRequest();
  const done = new Promise<StorageSaved>((resolve, reject) => {
    const query = new URLSearchParams({ path: folder, source });
    xhr.open('POST', `/api/storage/accounts/${encodeURIComponent(accountId)}/file?${query}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Genworx-Request', '1');
    xhr.setRequestHeader('X-Filename', encodeURIComponent(name));
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress(e.loaded / e.total);
      }
    };
    xhr.onload = () => {
      let payload: unknown;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        payload = undefined;
      }
      if (xhr.status === 201) {
        resolve(payload as StorageSaved);
        return;
      }
      if (xhr.status === 401) {
        window.dispatchEvent(new Event('genworx:session-ended'));
      }
      const body = payload as { message?: string; error?: string } | undefined;
      reject(
        new ApiError(
          xhr.status,
          body?.error ?? 'failed',
          xhr.status === 401
            ? 'your session has ended'
            : (body?.message ?? 'the file was not saved'),
        ),
      );
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', 'the connection was lost'));
    xhr.onabort = () => reject(new DOMException('The upload was cancelled.', 'AbortError'));
    xhr.send(file);
  });
  return { done, abort: () => xhr.abort() };
}

/** How a provider is named to people. */
export const KIND_NAMES = {
  seafile: 'Seafile',
  nextcloud: 'Nextcloud',
  ocis: 'ownCloud',
} as const;
